import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef } from "react";

import { cn } from "../lib/cn";
import { surfaceVariants, type SurfaceVariant } from "./surface";

/** Même arbitrage que `Surface` : ni `box-shadow` ni `backdrop-filter` en
 *  transition — l'un se repeint, l'autre re-floute l'arrière-plan à chaque
 *  image. L'ombre de survol passe par `lift` (fondu d'opacité). */
const cardVariants = cva(
  "rounded-xl transform-gpu transition-[transform,opacity,border-color] duration-normal ease-standard",
  {
    variants: {
      variant: {
        /** Style d'origine : bordure fine, arête spéculaire et fond surélevé. */
        outline: "border border-border bg-surface-raised shadow-xs inset-shadow-raised",
        /** Surélévation tactile native avec ombre discrète. */
        raised: "bg-surface-raised border border-border/80 shadow-xs inset-shadow-raised",
        /** Fond en retrait : encadrés secondaires, blocs d'aide. */
        sunken: "bg-surface-sunken border border-border/40 inset-shadow-control",
        /** Surface verre dépoli (glassmorphism) natif avec flou d'arrière-plan. */
        glass:
          "bg-surface-raised/80 dark:bg-surface-raised/65 backdrop-blur-md border border-border/60 shadow-xs inset-shadow-glass",
        /** Surface neutre affleurante. */
        flat: "bg-surface border border-border/50",
      },
      interactive: {
        true: "lift cursor-pointer hover:-translate-y-px hover:will-change-transform hover:border-border-strong active:translate-y-0 active:duration-instant focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        false: "",
      },
    },
    defaultVariants: { variant: "outline", interactive: false },
  },
);

export interface CardProps
  extends ComponentPropsWithRef<"div">,
    VariantProps<typeof cardVariants> {
  /** Surface sémantique du composant. Préciser `surface` applique le matériau natif correspondant. */
  surface?: SurfaceVariant;
}

/**
 * Conteneur de surface. Composé plutôt que configuré : `Card` + `CardHeader` +
 * `CardBody` + `CardFooter` restent flexibles sans exploser en variantes.
 */
export function Card({ className, variant, surface, interactive, ...props }: CardProps) {
  const surfaceClass = surface
    ? surfaceVariants({ variant: surface, interactive })
    : cardVariants({ variant, interactive });

  return <div className={cn("rounded-xl", surfaceClass, className)} {...props} />;
}

export function CardHeader({ className, ...props }: ComponentPropsWithRef<"div">) {
  return (
    <div className={cn("flex flex-col gap-1 border-b border-border p-5", className)} {...props} />
  );
}

export function CardTitle({ className, ...props }: ComponentPropsWithRef<"h3">) {
  return (
    <h3 className={cn("text-base font-semibold tracking-tight text-fg", className)} {...props} />
  );
}

export function CardDescription({ className, ...props }: ComponentPropsWithRef<"p">) {
  return <p className={cn("text-sm text-fg-muted", className)} {...props} />;
}

export function CardBody({ className, ...props }: ComponentPropsWithRef<"div">) {
  return <div className={cn("p-5", className)} {...props} />;
}

export function CardFooter({ className, ...props }: ComponentPropsWithRef<"div">) {
  return (
    <div
      className={cn("flex items-center gap-3 border-t border-border p-5", className)}
      {...props}
    />
  );
}

export { cardVariants };
